import { LIFECYCLE_LABELS, formatPhone } from '@takumi/shared';
import { useQuery } from '@tanstack/react-query';
import { Building2, Search, User } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Spinner } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { LifecycleStage } from '@takumi/shared';

interface SearchResult {
  organizations: { id: string; name: string; city: string | null; lifecycleStage: LifecycleStage; stageName: string; stageColor: string }[];
  contacts: { id: string; fullName: string; phoneE164: string | null; email: string | null; organizationId: string; organizationName: string }[];
}

/** Ctrl+K / "/" global search across businesses and people (name, phone, email, VAT id). */
export function GlobalSearch() {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
      if ((e.key === 'k' && (e.ctrlKey || e.metaKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResult>('/search', { q: debounced }),
    enabled: debounced.length >= 2,
    staleTime: 10_000,
  });

  const items = useMemo(
    () => [
      ...(data?.organizations ?? []).map((o) => ({ key: `o${o.id}`, to: `/org/${o.id}`, kind: 'org' as const, title: o.name, sub: [o.city, LIFECYCLE_LABELS[o.lifecycleStage], o.stageName].filter(Boolean).join(' · '), color: o.stageColor })),
      ...(data?.contacts ?? []).map((c) => ({ key: `c${c.id}`, to: `/org/${c.organizationId}`, kind: 'contact' as const, title: c.fullName, sub: [c.organizationName, c.phoneE164 ? formatPhone(c.phoneE164) : c.email].filter(Boolean).join(' · '), color: undefined })),
    ],
    [data],
  );

  const go = (to: string) => {
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
    navigate(to);
  };

  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
      <input
        ref={inputRef}
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, items.length - 1));
          else if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0));
          else if (e.key === 'Enter' && items[active]) go(items[active].to);
          else if (e.key === 'Escape') inputRef.current?.blur();
        }}
        placeholder="חיפוש עסק, איש קשר, טלפון…"
        aria-label="חיפוש"
        className="h-9 w-full rounded-control border border-line bg-surface ps-9 pe-14 text-sm placeholder:text-faint focus:border-matcha-500 focus:ring-2 focus:ring-matcha-200 focus:outline-none"
      />
      <kbd className="pointer-events-none absolute end-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-line bg-sunken px-1.5 text-[10px] font-semibold text-muted sm:block" dir="ltr">
        Ctrl K
      </kbd>
      {open && debounced.length >= 2 && (
        <div className="absolute inset-x-0 top-full z-50 mt-1.5 max-h-96 overflow-y-auto rounded-card border border-line bg-surface p-1 shadow-pop animate-pop-in">
          {isFetching && !data && (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          )}
          {data && items.length === 0 && <p className="p-4 text-center text-sm text-muted">לא נמצאו תוצאות עבור "{debounced}"</p>}
          {items.map((it, i) => (
            <button
              key={it.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(it.to)}
              onMouseEnter={() => setActive(i)}
              className={cn('flex w-full items-center gap-3 rounded-md px-3 py-2 text-start', i === active && 'bg-matcha-50')}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-sunken text-muted [&_svg]:size-4">
                {it.kind === 'org' ? <Building2 /> : <User />}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">{it.title}</span>
                <span className="block truncate text-xs text-muted">{it.sub}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
